import type { PropsWithChildren } from "react";
import { createContext, useEffect, useMemo, useState } from "react";
import { CssBaseline, ThemeProvider } from "@mui/material";
import { QueryClientProvider } from "@tanstack/react-query";
import { ApiProvider, createQueryClient } from "@aeriosiq/api-client";
import {
  darkTheme,
  lightTheme,
  useColorScheme,
  type UseColorSchemeResult,
} from "@aeriosiq/ui";
import { api } from "../api/client.js";

export const ColorSchemeContext = createContext<UseColorSchemeResult | null>(
  null,
);

export function Providers({ children }: PropsWithChildren) {
  const [queryClient] = useState(() => createQueryClient());
  const colorScheme = useColorScheme();
  const theme = useMemo(
    () => (colorScheme.scheme === "dark" ? darkTheme : lightTheme),
    [colorScheme.scheme],
  );
  // Mirror the active color scheme onto `<html data-theme="…">` so the
  // CSS variables declared in `global.css` (`:root[data-theme="dark"]`)
  // resolve to the dark palette. Without this the variables fall back to
  // the `:root` light defaults, the body bg stays white, and any page
  // that scrolls past the in-canvas content reveals a white gutter at
  // the bottom (recurring symptom across Stories 054 / 051 / 059).
  useEffect(() => {
    if (typeof document === "undefined") return;
    document.documentElement.dataset.theme = colorScheme.scheme;
  }, [colorScheme.scheme]);
  return (
    <QueryClientProvider client={queryClient}>
      <ApiProvider client={api}>
        <ColorSchemeContext.Provider value={colorScheme}>
          <ThemeProvider theme={theme}>
            <CssBaseline />
            {children}
          </ThemeProvider>
        </ColorSchemeContext.Provider>
      </ApiProvider>
    </QueryClientProvider>
  );
}
